"use client";

import React, { useState } from "react";
import { DialogLayout } from "../../ui/layouts/DialogLayout";
import { TextField } from "../../ui/components/TextField";
import { Button } from "../../ui/components/Button";
import { DiscordLoginButton, GoogleLoginButton } from "react-social-login-buttons";
import { createClient } from "@supabase/supabase-js";
import { handleProviderSignIn, supabase } from "services/Account/Supabase";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isSignUp?: boolean; // Determines whether it's sign-up or login
}

function AuthModal({ open, onOpenChange, isSignUp = false }: AuthModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const emailEnabled = false;

  /**
   * Handles signing up a new user with email/password
   */
  const signUpNewUser = async () => {
    setLoading(true);
    setErrorMessage(null);

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setLoading(false);
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/welcome`,
      },
    });

    setLoading(false);

    if (error) {
      setErrorMessage(error.message);
    } else {
      alert("Check your email for the confirmation link!");
      onOpenChange(false);
    }
  };

  /**
   * Handles signing in an existing user with email/password
   */
  const signInWithEmail = async () => {
    setLoading(true);
    setErrorMessage(null);

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (error) {
      setErrorMessage(error.message);
    } else {
      alert("Successfully logged in!");
      onOpenChange(false);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange} className="z-50">
      <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-default-background px-6 py-12">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-8">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-heading-1 font-heading-1 text-default-font">
              {isSignUp ? "Create account" : "Log in"}
            </span>
            <span className="text-body font-body text-subtext-color">
              {isSignUp ? "Get started with your free account" : "Welcome back! Log in to continue"}
            </span>
          </div>

          {/* Display Error Messages */}
          {errorMessage && (
            <span className="text-red-500 text-sm">{errorMessage}</span>
          )}
          {emailEnabled && (<div>
            <div className="flex w-full flex-col items-start gap-4">
              <TextField className="h-auto w-full flex-none" variant="filled" label="Email" icon="FeatherMail">
                <TextField.Input
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </TextField>
              <TextField className="h-auto w-full flex-none" variant="filled" label="Password" icon="FeatherLock">
                <TextField.Input
                  type="password"
                  placeholder={isSignUp ? "Choose a password" : "Enter your password"}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  name={isSignUp ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </TextField>

              {/* Confirm Password Field for Sign-Up */}
              {isSignUp && (
                <TextField className="h-auto w-full flex-none" variant="filled" label="Confirm Password" icon="FeatherLock">
                  <TextField.Input
                    type="password"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    name="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </TextField>
              )}

              {/* Auth Button */}
              <Button
                className="h-10 w-full flex-none"
                size="large"
                disabled={loading}
                onClick={isSignUp ? signUpNewUser : signInWithEmail}
              >
                {loading ? "Processing..." : isSignUp ? "Sign up" : "Log in"}
              </Button>
            </div>

            {/* Divider */}
            <div className="flex w-full items-center gap-4">
              <div className="flex h-px grow shrink-0 basis-0 items-start bg-neutral-border" />
              <span className="text-body font-body text-subtext-color">or</span>
              <div className="flex h-px grow shrink-0 basis-0 items-start bg-neutral-border" />
            </div></div>)}

          {/* Social Login Buttons */}
          <div className="flex flex-col w-full font-['Montserrat'] font-medium">
            <GoogleLoginButton onClick={() => { handleProviderSignIn('google') }} />
            <DiscordLoginButton onClick={() => { handleProviderSignIn('discord') }} />
          </div>
        </div>
      </div>
    </DialogLayout>
  );
}

export default AuthModal;
